<template>
  <v-container class="fill-height fullscreen-bg" fluid> 
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="6">
        <v-card v-if="isRedirecting && !errorCode" class="elevation-12 pa-4 text-center">
          <v-card-title class="headline d-block" style="">{{$t('account.preparing-title')}}</v-card-title>
          <v-card-text class="">
            <p>{{$t('account.preparing-text')}}</p>
            <v-progress-circular indeterminate :size="100" :width="5" color="primary" class="mx-a"></v-progress-circular>
          </v-card-text>
        </v-card>
        <v-card v-else-if="isRedirecting && errorCode == 'ALREADY_CONNECTED'" class="elevation-12 pa-4 text-center">
          <v-card-title class="headline d-block" style="">{{$t('account.connect-issue')}}</v-card-title>
          <v-alert type="warning" prominent>
            {{$t('account.already-connected-message')}}
          </v-alert>
          <v-card-text class="">
            <p>{{$t('account.already-connected-description-1')}}</p>
            <p>{{$t('account.already-connected-description-2')}}</p>
            <v-btn outlined :to="{name: 'profileConnections'}" color="primary">
              {{$t('profile.connections.manage')}}
            </v-btn>
          </v-card-text>
        </v-card>
        <v-card v-else-if="isRedirecting && errorCode" class="elevation-12 pa-4 text-center">
          <v-card-title class="headline d-block" style="">{{$t('account.connect-issue')}}</v-card-title>
          <v-alert type="warning" prominent>{{$t('account.connect-issue-message')}}</v-alert>
          <v-card-text class="">
            <p>{{$t('account.connect-issue-description')}}</p>
            <v-btn outlined :to="{name: 'profileConnections'}" color="primary">
              {{$t('profile.connections.manage')}}
            </v-btn>
          </v-card-text>
        </v-card>
        <v-card v-else class="elevation-12">
          <EventHeader v-if="event" :event="event" compact/>
          <v-card-title class="headline">{{$t('account.login.title')}}</v-card-title>
          <div class="provider-container">
            <v-card-text class="provider pb-0" :style="{order:providerOrder('sodisp'), display: providerDisplay('sodisp')}">
              <v-alert v-if="error" type="error">{{error}}</v-alert>
              <v-form ref="form" v-model="valid" lazy-validation>
                <v-text-field 
                  v-model="email" 
                  :rules="emailRules" 
                  :label="$t('account.register.email')" 
                  prepend-icon="fa-envelope"
                  required>
              </v-text-field>

                <v-text-field
                  v-model="password"
                  :rules="passwordRules"
                  :label="$t('account.register.password')"
                  required
                  prepend-icon="fa-lock"
                  :append-icon="passwordShow ? 'far fa-eye-slash' : 'far fa-eye'"
                  :type="passwordShow ? 'text' : 'password'"
                  @click:append="passwordShow = !passwordShow"
                ></v-text-field>

              </v-form>
              <v-btn block :disabled="!valid" color="primary" @click="validate">{{$t('account.login.submit')}}</v-btn>
              <v-btn text class="mt-2" color="primary" to="/account/passwordrecovery">{{$t('account.login.forgot-your-password')}}</v-btn>
            </v-card-text>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('strava'), display: providerDisplay('strava')}">
              <v-btn v-if="providerUrls" :href="providerUrls.strava" block color="#FC4C02">
                <img src="/images/btn_strava_connectwith_orange.svg" style="height:42px"/>
              </v-btn>
            </div>

            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('garmin'), display: providerDisplay('garmin')}">
              <v-btn v-if="providerUrls" :href="providerUrls.garmin" block color="#11BFED">
                <img src="/images/garmin-connect.png" width="26" height="26" alt="Garmin Connect">
                <v-icon  left>mdi-pencil</v-icon> Garmin Connect
              </v-btn>
            </div>

            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('polar'), display: providerDisplay('polar')}">
              <v-btn v-if="providerUrls" :href="providerUrls.polar" block color="#d10027" class="white--text">
                <span style="font-size:70%;">Connect with</span>
                <img src="/images/polar.png" height="22" alt="Polar" class="ml-2">
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('fitbit'), display: providerDisplay('fitbit')}">
              <v-btn v-if="providerUrls" :href="providerUrls.fitbit" block color="#4cc2c4" class="white--text">
                <span style="font-size:70%;">Connect with</span>
                <img src="/images/fitbit.png" height="22" alt="Fitbit" class="ml-2">
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('suunto'), display: providerDisplay('suunto')}">
              <v-btn v-if="providerUrls" :href="providerUrls.suunto" block color="#147eab" class="white--text">
                <span style="font-size:70%;">Connect with</span>
                <img src="/images/suunto.png" height="24" alt="Suunto" class="ml-2">
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('underarmour'), display: providerDisplay('underarmour')}">
              <v-btn v-if="providerUrls" :href="providerUrls.underarmour" block color="#ea212e" class="white--text">
                <img src="/images/underarmour.png" height="20" alt="Under Armour" class="mr-2">
                <span style="font-size:80%;" class="mr-1">Log In with</span>
                <strong style="font-size:80%; text-transform:uppercase;">Under Armour</strong>
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('coros'), display: providerDisplay('coros')}">
              <v-btn v-if="providerUrls" :href="providerUrls.coros" block color="#F8283B" class="white--text">
                <span style="font-size:70%;">Connect with</span>
                <img src="/images/coros.png" height="16" alt="Coros" class="ml-2">
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('eventscom'), display: providerDisplay('eventscom')}">
              <v-btn v-if="providerUrls" :href="providerUrls.eventscom" block color="#00BCDA" class="white--text">
                <span style="font-size:70%;">Connect with</span>
                <img src="/images/eventscom-white.png" height="16" alt="Evens.com" class="ml-2">
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('concept2'), display: providerDisplay('concept2')}">
              <v-btn v-if="providerUrls" :href="providerUrls.concept2" block color="#033B78" class="white--text">
                <span style="font-size:80%;" class="mr-1">Connect with</span>
                <strong style="font-size:80%; text-transform:uppercase;">Concept2</strong>
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('asdeporte'), display: providerDisplay('asdeporte')}">
              <v-btn v-if="providerUrls" :href="providerUrls.asdeporte" block color="#FF6A00" class="white--text">
                <span style="font-size:80%;" class="mr-1">Connect with</span>
                <strong style="font-size:80%; text-transform:uppercase;">Asdeporte</strong>
              </v-btn>
            </div>

          </div>
          <i18n path="account.register.terms_privacy" tag="v-card-text" class="text-muted">
            <router-link to='termsofservice'>{{$t('account.register.terms_privacy_terms')}}</router-link>
            <router-link to='privacypolicy'>{{$t('account.register.terms_privacy_privacy')}}</router-link>
          </i18n>
          <v-divider />
          <v-card-text class="text-center">
            <p>{{$t('account.login.create-account-intro')}}</p>
            <v-btn outlined block color="primary" :to="{ name: 'register', query: $route.query }">{{$t('account.login.create-an-account')}}</v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/auth'
import profileService from "@/services/profileService";
import eventService from "@/services/eventService";
import EventHeader from '@/components/EventHeader.vue';
import tenants from '@/data/tenants.config'
import i18n from '@/i18n'
const tenant = tenants.current();

export default {
  components: {
    EventHeader,
  },
  props: {
  },
  data: () => ({
    providers: tenant.accountProviders,
    providerUrls: null,
    event: null,
    passwordShow: false,
    valid: true,
    error: null,
    errorCode: null,
    email: "",
    emailRules: [
      v => !!v || i18n.t('account.register.email-is-required'),
      v => /.+@.+/.test(v) || i18n.t('account.register.email-must-be-valid')
    ],
    password: "",
    passwordRules: [v => !!v || i18n.t('account.register.password-is-required')]
  }),

  async mounted() {
    var token = this.$route.query.token;
    this.errorCode = this.$route.query.error;
    if (token && !this.errorCode) {
      // Auto-login from redirects with server-side oauth flows
      var response = await firebase.auth().signInWithCustomToken(token);
      if (response && response.user) {
        this.$store.dispatch("setUser", response.user);
        this.$gtag.event('login', { method : 'Token' });
        this.redirect();
        return;
      }
    }
    if (this.$route.query.providers) {
      this.providers = this.$route.query.providers.replace(' ', '').split(',');
    }
    if (this.$route.query.event) {
      this.event = (await eventService.get(this.$route.query.event)).data;
      if (this.event && this.event.allowed_providers && this.event.allowed_providers.length > 0 && !this.$route.query.providers) {
        const customProviders = this.event.allowed_providers;
        customProviders.push('sodisp');
        this.providers = customProviders;
      }
    }

    const options = { 
      referral: this.$route.query.referral, 
      eventId: this.$route.query.event, 
      verificationCode: this.$route.query.verificationCode, 
      view: this.viewMode
    };
    this.providerUrls = this.$oauth.getProviderUrls(options, this.event);

    console.log('Loading providers', this.providers);
    if (this.providers && this.providers.length == 1) {
      const provider = this.providers[0];
      if (this.$oauth.shouldAutoRedirect(provider)) {
        console.log('Only one provider found, auto redirect Redirecting to', this.providerUrls[provider]);
        window.location.href = this.providerUrls[provider];
        return;
      }
    }
  },

  methods: {
    providerOrder(provider) {
      return this.providers.indexOf(provider);
    },
    providerDisplay(provider) {
      return this.providers.includes(provider) ? '' : 'none';
    },

    async validate() {
      if (this.$refs.form.validate()) {
        this.snackbar = true;
        this.loginWithFirebase();
      }
    },

    reset() {
      this.$refs.form.reset();
    },

    redirect() {
      var redirect = this.$route.query.redirect;
      var eventId = this.$route.query.event;
      var verificationCode = this.$route.query.verificationCode;
      if (eventId) {
        this.$router.push({ name: 'eventWelcome', params: { id: eventId }, query: {verificationCode:verificationCode} });
        return;
      }
      if (redirect) {
        if (redirect === 'create-event'){
          this.$router.replace({ name: "eventmanagerCreate", query: { referral: this.$route.query.referral } });
          return;
        }
        else {
          window.location.href = redirect;
        }
      }
      this.$router.replace({ name: "profile" });
    },

    async loginWithFirebase() {
      const user = {
        email: this.email,
        password: this.password
      };
      try {
        var response = await firebase
          .auth()
          .signInWithEmailAndPassword(user.email, user.password);
        
        await this.$store.dispatch("setUser", response.user);
        this.$gtag.event('login', { method : 'Email' });

        var eventId = this.$route.query.event;
        if (eventId) {
          await profileService.joinEvent(eventId);
        }

        this.redirect();
      } catch (ex) {
        this.error = this.$t('account.login.invalid-credentials');
      }
    }
  },
  computed: {
    isRedirecting() {
      return this.$route.query.token;
    },
    viewMode() {
      // note: somehow vue doesn't correctly read it from route.query so get it manually
      const viewFromUrl = new URLSearchParams(window.location.search).get("view");
      return viewFromUrl || this.$route.query.view || this.$store.state.view;
    },

  },
};
</script>
<style lang="scss" scoped>
  .fullscreen-bg {
    margin-top: 60px;
    padding-top: 40px;
    background-image: url(https://sodisp.imgix.net/hero-cycling-1.jpg?w=2000&h=2000&fit=crop);
    background-size: cover;
    background-position: 50%;

    .row { margin: 0!important; }
  }
  .view--embedded .fullscreen-bg { margin-top: 0;}

  .provider-container {
    display: flex;
    flex-direction: column;

  }

</style>

